/**
 * API client for /api/web/v2/admin/* endpoints.
 *
 * All requests use `adminFetch` which always sets `cache: "no-store"` to
 * prevent the browser from sending conditional GETs (If-None-Match) that
 * Caddy or the browser itself satisfies with 304 before the request ever
 * reaches the API container.
 */

import type {
  ActivityPage,
  AdminUser,
  ChangeEmailRequest,
  OnboardRequest,
  OperationResult,
  PersonSummary,
  SetOnLeaveRequest,
} from "./types";

const BASE = `${import.meta.env.VITE_API_URL ?? ""}/api/web/v2/admin`;

function adminFetch(url: string, options?: RequestInit): Promise<Response> {
  return fetch(url, { ...options, credentials: "include", cache: "no-store" });
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${text.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

export async function fetchPeople(): Promise<PersonSummary[]> {
  return json(await adminFetch(`${BASE}/people`));
}

export async function fetchAdmins(): Promise<AdminUser[]> {
  return json(await adminFetch(`${BASE}/admins`));
}

export async function addAdmin(email: string): Promise<AdminUser> {
  return json(
    await adminFetch(`${BASE}/admins`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    }),
  );
}

export async function removeAdmin(email: string): Promise<void> {
  const res = await adminFetch(`${BASE}/admins/${encodeURIComponent(email)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${text.slice(0, 200)}`);
  }
}

export async function fetchPerson(email: string): Promise<PersonSummary> {
  return json(await adminFetch(`${BASE}/people/${encodeURIComponent(email)}`));
}

export async function checkPerson(email: string): Promise<PersonSummary> {
  return json(
    await adminFetch(`${BASE}/people/${encodeURIComponent(email)}/check`, {
      method: "POST",
    }),
  );
}

export async function offboardPerson(email: string): Promise<OperationResult> {
  return json(
    await adminFetch(`${BASE}/people/${encodeURIComponent(email)}/offboard`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }),
  );
}

export async function onboardPerson(
  email: string,
  body: OnboardRequest,
): Promise<OperationResult> {
  return json(
    await adminFetch(`${BASE}/people/${encodeURIComponent(email)}/onboard`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function setPersonOnLeave(
  email: string,
  body: SetOnLeaveRequest,
): Promise<OperationResult> {
  return json(
    await adminFetch(`${BASE}/people/${encodeURIComponent(email)}/leave`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function changePersonEmail(
  email: string,
  body: ChangeEmailRequest,
): Promise<OperationResult> {
  return json(
    await adminFetch(`${BASE}/people/${encodeURIComponent(email)}/change-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function fetchActivity(opts: {
  person?: string;
  app?: string;
  action?: string;
  result?: string;
  actor?: string;
  beforeTs?: string;
  dateFrom?: string;
} = {}): Promise<ActivityPage> {
  const params = new URLSearchParams();
  if (opts.person) params.set("person", opts.person);
  if (opts.app) params.set("app", opts.app);
  if (opts.action) params.set("action", opts.action);
  if (opts.result) params.set("result", opts.result);
  if (opts.actor) params.set("actor", opts.actor);
  if (opts.beforeTs) params.set("before_ts", opts.beforeTs);
  if (opts.dateFrom) params.set("date_from", opts.dateFrom);
  const qs = params.toString();
  return json(await adminFetch(`${BASE}/activity${qs ? `?${qs}` : ""}`));
}

export async function triggerDriftCheck(): Promise<{ alerts: number; checked: number; run_at: string }> {
  return json(
    await adminFetch(`${BASE}/drift-check`, { method: "POST" }),
  );
}

export async function transferOwnership(fromEmail: string, toEmail: string): Promise<AdminUser> {
  return json(
    await adminFetch(`${BASE}/admins/${encodeURIComponent(fromEmail)}/transfer-ownership`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to_email: toEmail }),
    }),
  );
}
